import { useState } from "react";
import { motion } from "framer-motion";
import { Flame, Briefcase, Globe2, CalendarDays } from "lucide-react";
import { Reveal, SectionHead, GoldButton } from "./Reveal";

const FILTERS = [
  { id: "all", label: "All Programs" },
  { id: "business", label: "For Brand Owners" },
  { id: "student", label: "For Students" },
  { id: "coach", label: "For Coaches & Healers" },
  { id: "freelancer", label: "For Freelancers" },
];

export const Courses = ({ courses, onCourse, onEnroll }) => {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all" ? courses : courses.filter((c) => c.audiences.includes(filter));

  return (
    <section id="courses" className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="courses-section">
      <SectionHead
        overline="The catalog"
        title={<>Live programs, <em className="text-gold-gradient not-italic">real outcomes.</em></>}
        sub="Certificate and diploma cohorts taught live on Google Meet — every course lists its metro salary path, freelance earning potential and future designations."
      />

      <Reveal delay={0.2} className="mt-10">
        <div className="flex flex-wrap gap-2.5" data-testid="course-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`course-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full px-5 py-2.5 text-xs sm:text-sm transition-colors duration-300 ${
                filter === f.id
                  ? "bg-leaf text-void font-semibold"
                  : "border border-white/15 text-neutral-400 hover:border-gold/50 hover:text-champagne"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {visible.map((c, i) => (
          <Reveal key={c.id} delay={(i % 3) * 0.08}>
            <motion.article
              whileHover={{ y: -8, rotate: -0.5 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className="card-sheen group flex h-full flex-col rounded-2xl border border-white/10 bg-surface/70 p-7 hover:border-leaf/50 hover:gold-glow transition-colors duration-500"
              data-testid={`course-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-sans text-xs ${
                  c.type === "Diploma" ? "bg-gold/15 text-champagne border border-gold/30" : "bg-white/5 text-neutral-300 border border-white/15"
                }`} data-testid={`course-type-${c.id}`}>
                  {c.type} · {c.duration}
                </span>
                {c.bestseller && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1.5 font-sans text-[11px] font-semibold text-void" data-testid={`course-bestseller-${c.id}`}>
                    <Flame size={11} /> Bestseller
                  </span>
                )}
              </div>

              <h3 className="mt-5 font-display text-xl sm:text-2xl leading-snug text-neutral-100">{c.title}</h3>
              <p className="mt-2 font-sans text-sm text-gold/90">{c.tag}</p>
              {c.highlights && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {c.highlights.map((h) => (
                    <span key={h} className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 font-sans text-[11px] text-neutral-400">{h}</span>
                  ))}
                </div>
              )}

              <div className="mt-6 space-y-2.5 text-sm text-neutral-400">
                <p className="flex items-center gap-2.5"><Briefcase size={14} className="text-gold shrink-0" /> Metro jobs: <span className="text-neutral-200">{c.salary}</span></p>
                <p className="flex items-center gap-2.5"><Globe2 size={14} className="text-gold shrink-0" /> Freelance: <span className="text-neutral-200">{c.freelance}</span></p>
                <p className="flex items-center gap-2.5"><CalendarDays size={14} className="text-gold shrink-0" /> Mode: <span className="text-neutral-200">Live on Google Meet</span></p>
              </div>

              <div className="mt-auto pt-7">
                <div className="flex items-baseline justify-between border-t border-white/10 pt-5">
                  <p className="font-display text-2xl text-champagne" data-testid={`course-price-${c.id}`}>₹{c.price.toLocaleString("en-IN")}</p>
                  <p className="font-sans text-xs text-neutral-500">Full program</p>
                </div>
                <div className="mt-5 flex gap-3">
                  <button
                    data-testid={`course-details-${c.id}`}
                    onClick={() => onCourse(c)}
                    className="flex-1 rounded-full border border-white/20 py-3 text-sm text-neutral-200 hover:border-gold/60 hover:text-champagne transition-colors duration-300"
                  >
                    View Details
                  </button>
                  <GoldButton testId={`course-enroll-${c.id}`} onClick={() => onEnroll(c)} className="flex-1 px-0">
                    Enroll Now
                  </GoldButton>
                </div>
              </div>
            </motion.article>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
